import { XIcon } from 'lucide-react'
import { createPortal } from 'react-dom'

import { Button, IconButton } from 'slab'

import { type MedicalPlanDisplayItem } from '../medicalPlanTypes'
import { clearPlansForComparison, storePlansForComparison } from '../utils/planComparisonStorage'

// TODO rename the file to PlanComparisonSidebarWidget
interface PlanComparisonSidebarWidgetProps {
  selectedPlansForComparison: MedicalPlanDisplayItem[];
  onCompareClick: () => void;
  onClearAll: () => void;
  onRemovePlan: (planId: MedicalPlanDisplayItem) => void;
}

export function PlanComparisonSidebarWidget ({
  selectedPlansForComparison,
  onCompareClick,
  onClearAll,
  onRemovePlan,
}: PlanComparisonSidebarWidgetProps) {
  if (selectedPlansForComparison.length === 0) {
    return null
  }

  const handleClearAll = () => {
    clearPlansForComparison()
    onClearAll()
  }

  const handleRemovePlan = (plan: MedicalPlanDisplayItem) => {
    const newPlans = selectedPlansForComparison.filter(p => p.plan.id !== plan.plan.id)
    storePlansForComparison(newPlans)
    onRemovePlan(plan)
  }

  const content = (
    <div className='p-4 border border-gray-200 shadow-xl xs:shadow-lg rounded-lg bg-white'>
      <div className='flex justify-between items-center mb-2'>
        <h3 className='text-gray-800 body-sm-bold'>
          Plan comparison
        </h3>
        <button
          onClick={handleClearAll}
          className='body-sm-bold text-[#9C3BFF] hover:text-[#6720B0]'
        >
          Clear all
        </button>
      </div>
      <ul className='space-y-2 mb-4'>
        {selectedPlansForComparison.map((plan) => (
          <li
            key={plan.plan.id}
            className='py-2 pl-2 pr-1 border border-gray-200 rounded-lg flex flex-row justify-between items-center'
          >
            <div className='xs:flex xs:flex-col min-w-0 text-nowrap xs:text-wrap truncate'>
              <span className='body-sm-bold text-gray-800'>
                {plan.plan.friendly_name}
              </span>
              {' '}
              <span className='body-sm text-gray-500'>
                {plan.plan.carrier_name}
              </span>
            </div>
            <div className='flex h-6 items-center'>
              <IconButton
                icon={XIcon}
                label='Remove'
                onClick={() => handleRemovePlan(plan)}
              />
            </div>
          </li>
        ))}
      </ul>
      {selectedPlansForComparison.length === 1 && (
        <p className='caption text-gray-800 text-center mb-1'>
          Select at least one more plan
        </p>
      )}
      <Button
        variant='primary'
        size='small'
        onClick={onCompareClick}
        disabled={selectedPlansForComparison.length < 2}
        width='full'
      >
        {selectedPlansForComparison.length > 1
          ? `Compare ${selectedPlansForComparison.length} plans`
          : 'Compare'}
      </Button>
    </div>
  )

  const portalTarget = document.getElementById('plan-comparison-portal')
  if (!portalTarget) {
    return null
  }

  return createPortal(content, portalTarget)
}
